<template>
    <div class="app-wrapper">
        <!-- Floating Glassmorphism Navbar -->
        <nav class="glass-nav" :class="{ 'nav-scrolled': isScrolled, 'nav-open': isMenuOpen }">
            <div class="nav-container">
                <a href="#home" class="nav-logo" @click.prevent="scrollTo('home'); isMenuOpen = false">HP.</a>
                
                <!-- Desktop Links -->
                <div class="nav-links desktop-links">
                    <a href="#about" @click.prevent="scrollTo('about')">About</a>
                    <a href="#skills" @click.prevent="scrollTo('skills')">Skills</a>
                    <router-link to="/blogs">Blogs</router-link>
                    <ThemeToggle />
                </div>

                <!-- Mobile Menu Toggle -->
                <button class="menu-toggle" @click="isMenuOpen = !isMenuOpen" aria-label="Toggle menu">
                    <i :class="isMenuOpen ? 'bi bi-x-lg' : 'bi bi-list'"></i>
                </button>
            </div>

            <!-- Mobile Links Dropdown -->
            <transition name="fade">
                <div class="mobile-links" v-if="isMenuOpen">
                    <a href="#about" @click.prevent="scrollTo('about'); isMenuOpen = false">About</a>
                    <a href="#skills" @click.prevent="scrollTo('skills'); isMenuOpen = false">Skills</a>
                    <router-link to="/blogs" @click="isMenuOpen = false">Blogs</router-link>
                    <div class="mobile-theme-toggle">
                        <ThemeToggle />
                    </div>
                </div>
            </transition>
        </nav>

        <main class="content-sections">
            <!-- Hero Section -->
            <section id="home" class="hero-section reveal">
                <div class="hero-bg"></div>
                <div class="container hero-content">
                    <span class="hero-eyebrow">Data Scientist & Researcher</span>
                    <h1 class="hero-title">Hitesh <span class="accent">Pradhan</span></h1>
                    <p class="hero-subtitle">
                        Bridging the gap between <strong>healthcare</strong> and 
                        <strong>data science</strong>.
                    </p>
                    <div class="hero-cta">
                        <button class="btn-primary" @click="scrollTo('about')">Explore My Work</button>
                    </div>
                </div>
            </section>

            <!-- About Section -->
            <section id="about" class="about-section reveal">
                <div class="container">
                    <div class="section-header">
                        <h2 class="section-title">About Me</h2>
                        <div class="title-underline"></div>
                    </div>
                    
                    <div class="about-grid">
                        <div class="about-image-wrap">
                            <div class="image-border"></div>
                            <img src="../assets/profile-picture.jpg" alt="Hitesh Pradhan" class="profile-img" />
                        </div>
                        <div class="about-text">
                            <p class="bio-main">
                                I am a data scientist and researcher driven by the potential of 
                                <span class="highlight">AI and healthcare</span>.
                            </p>
                            <p class="bio-sub">
                                Beyond my professional work, I build open-source tools that empower other researchers 
                                and developers to push the boundaries of what's possible in health technology.
                            </p>
                            
                            <div class="expertise-grid">
                                <div class="expertise-card">
                                    <div class="card-icon"><i class="bi bi-activity"></i></div>
                                    <h3>Data Scientist</h3>
                                    <p>Analyzing healthcare data to uncover deep insights.</p>
                                </div>
                                <div class="expertise-card">
                                    <div class="card-icon"><i class="bi bi-code-slash"></i></div>
                                    <h3>Researcher</h3>
                                    <p>Advancing cognitive health research through community-focused innovations.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Skills Section -->
            <section id="skills" class="skills-section reveal">
                <div class="container">
                    <div class="section-header">
                        <h2 class="section-title">Technical Expertise</h2>
                        <div class="title-underline"></div>
                    </div>

                    <div class="skills-grid">
                        <div v-for="(group, name) in skillGroups" :key="name" class="skill-card reveal">
                            <div class="card-header">
                                <div class="category-icon">
                                    <i :class="getCategoryIcon(name)"></i>
                                </div>
                                <h3 class="group-name">{{ name }}</h3>
                            </div>
                            <div class="skills-pill-box">
                                <span v-for="skill in group" :key="skill.name" class="skill-pill">
                                    <i :class="skill.icon"></i>
                                    {{ skill.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Footer -->
            <footer id="footer" class="main-footer">
                <div class="container">
                    <div class="footer-content">
                        <p class="footer-copy">&copy; 2026 Hitesh Pradhan. Crafted with passion.</p>
                        <div class="footer-socials">
                            <a href="https://github.com/pradhanhitesh"><i class="bi bi-github"></i></a>
                            <a href="https://www.linkedin.com/in/pradhanhitesh/"><i class="bi bi-linkedin"></i></a>
                            <a href="mailto:htshpradhan5@gmail.com"><i class="bi bi-envelope"></i></a>
                        </div>
                    </div>
                </div>
            </footer>
        </main>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import ThemeToggle from "./ThemeToggle.vue";

const isScrolled = ref(false);
const isMenuOpen = ref(false);

const skillGroups = {
    "Scientific Research": [
        { name: "MATLAB", icon: "bi bi-code-square" },
        { name: "R", icon: "bi bi-graph-up-arrow" },
        { name: "Neuroimaging", icon: "bi bi-images" },
        { name: "Community Research", icon: "bi bi-people" }
    ],
    "Software Engineering": [
        { name: "Python", icon: "bi bi-braces" },
        { name: "Flask", icon: "bi bi-server" },
        { name: "PostgreSQL", icon: "bi bi-database" },
        { name: "GitHub", icon: "bi bi-github" }
    ],
    "Data & AI": [
        { name: "NLP", icon: "bi bi-list-nested" },
        { name: "LLM", icon: "bi bi-claude" },
        { name: "scikit-learn", icon: "bi bi-diagram-3" },
        { name: "Polars", icon: "bi bi-table" },
    ]
};

const getCategoryIcon = (category) => {
    const icons = {
        "Scientific Research": "bi bi-mortarboard",
        "Software Engineering": "bi bi-code-slash",
        "Data & AI": "bi bi-bar-chart-steps"
    };
    return icons[category] || "bi bi-stars";
};

const handleScroll = () => {
    const scrollY = window.scrollY;
    isScrolled.value = scrollY > 50;
    
    // Skip parallax on mobile for stability
    if (window.innerWidth < 768) return;

    const progress = Math.min(scrollY / 400, 1);
    const nav = document.querySelector('.glass-nav');
    if (nav) {
        nav.style.setProperty('--nav-width', `${90 + (progress * 10)}%`);
        nav.style.setProperty('--nav-max-width', `${1000 + (progress * 1000)}px`);
        nav.style.setProperty('--nav-top', `${1.5 - (progress * 1.5)}rem`);
        nav.style.setProperty('--nav-radius', `${(1 - progress) * 50}px`);
        nav.style.setProperty('--nav-padding', `${0.75 + (progress * 0.25)}rem ${2 + (progress * 2)}rem`);
    }
};

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
        window.scrollTo({
            top: el.offsetTop + 100,
            behavior: "smooth"
        });
    }
};

onMounted(() => {
    window.addEventListener("scroll", handleScroll);
    
    // Intersection Observer for reveal animations
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add("active");
            }
        });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal").forEach(el => observer.observe(el));
});

onBeforeUnmount(() => {
    window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
:root {
    --bg-primary: #fdfdfd;
    --text-main: #1a1a1a;
    --text-muted: #666;
    --accent: #0d6efd;
    --accent-soft: rgba(13, 110, 253, 0.08);
    --glass: rgba(255, 255, 255, 0.7);
    --transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.app-wrapper {
    background-color: var(--bg-primary);
    color: var(--text-main);
    font-family: 'Inter', sans-serif;
    line-height: 1.6;
    overflow-x: hidden;
    transition: background-color 0.4s ease, color 0.4s ease;
}

.container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* Navbar */
.glass-nav {
    position: fixed;
    top: var(--nav-top, 1.5rem);
    left: 50%;
    transform: translateX(-50%);
    width: var(--nav-width, 90%);
    max-width: var(--nav-max-width, 1000px);
    z-index: 1000;
    padding: var(--nav-padding, 0.75rem 2rem);
    border-radius: var(--nav-radius, 999px);
    background: var(--nav-bg);
    backdrop-filter: blur(12px);
    border: 1px solid var(--nav-border);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
    transition: background 0.4s ease, border 0.4s ease, box-shadow 0.4s ease, top 0.4s ease, width 0.4s ease;
}

.glass-nav.nav-scrolled {
    background: var(--nav-bg);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
    border-color: var(--border-color);
}

.nav-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1100px;
    margin: 0 auto;
    width: 100%;
}

.nav-logo {
    font-family: 'Outfit', sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--text-main);
    text-decoration: none;
    letter-spacing: -1px;
}

.nav-links {
    display: flex;
    gap: 2rem;
}

.nav-links a {
    text-decoration: none;
    color: var(--text-muted);
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.nav-links a:hover {
    color: var(--accent);
}

/* Hero Section */
.hero-section {
    position: relative;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    overflow: hidden;
}

.hero-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 80% 20%, var(--accent-soft) 0%, transparent 40%),
                radial-gradient(circle at 10% 80%, var(--accent-soft) 0%, transparent 40%);
    z-index: -1;
}

.hero-eyebrow {
    display: block;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.2rem;
    color: var(--accent);
    margin-bottom: 1rem;
    font-weight: 600;
}

.hero-title {
    font-family: 'Outfit', sans-serif;
    font-size: 4.5rem;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    letter-spacing: -2px;
}

.hero-title .accent {
    color: var(--accent);
}

.hero-subtitle {
    font-size: 1.25rem;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto 2.5rem;
}

.hero-cta {
    display: flex;
    gap: 1rem;
    justify-content: center;
}

.btn-primary {
    background: var(--accent);
    color: #fff;
    border: none;
    padding: 0.8rem 2rem;
    border-radius: 999px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px var(--accent-soft);
}

.btn-secondary {
    background: transparent;
    color: var(--text-main);
    border: 1px solid var(--border-color);
    padding: 0.8rem 2rem;
    border-radius: 999px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.btn-secondary:hover {
    background: #f8f9fa;
    border-color: #1a1a1a;
}

/* Sections General */
.section-header {
    margin-bottom: 4rem;
    text-align: center;
}

.section-title {
    font-family: 'Outfit', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
}

.title-underline {
    width: 60px;
    height: 4px;
    background: var(--accent);
    margin: 0 auto;
    border-radius: 2px;
}

/* About Section */
.about-section {
    padding: 10rem 0;
}

.about-grid {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: 5rem;
    align-items: center;
}

.about-image-wrap {
    position: relative;
    padding: 1rem;
}

.image-border {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid var(--accent);
    border-radius: 2rem;
    z-index: -1;
    transform: rotate(-3deg);
}

.profile-img {
    width: 100%;
    border-radius: 1.5rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.bio-main {
    font-size: 1.5rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
}

.bio-sub {
    color: var(--text-muted);
    margin-bottom: 3rem;
}

.highlight {
    color: var(--accent);
    font-weight: 600;
}

.expertise-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
}

.expertise-card {
    padding: 2rem;
    background: var(--card-bg);
    border-radius: 1.5rem;
    border: 1px solid var(--border-color);
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.expertise-card:hover {
    border-color: var(--accent);
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
}

.card-icon {
    font-size: 2rem;
    color: var(--accent);
    margin-bottom: 1rem;
}

.expertise-card h3 {
    font-family: 'Outfit', sans-serif;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
}

.expertise-card p {
    font-size: 0.9rem;
    color: var(--text-muted);
    margin: 0;
}

/* Skills Section */
.skills-section {
    padding: 10rem 0;
    background: radial-gradient(circle at 10% 90%, var(--accent-soft) 0%, transparent 30%);
}

.skills-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
}

.skill-card {
    background: var(--glass);
    backdrop-filter: blur(10px);
    border: 1px solid var(--nav-border);
    border-radius: 2.5rem;
    padding: 3rem 2rem;
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.skill-card:hover {
    transform: translateY(-12px) scale(1.02);
    background: var(--card-bg);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.08);
    border-color: var(--accent);
}

.card-header {
    margin-bottom: 2.5rem;
}

.category-icon {
    width: 70px;
    height: 70px;
    background: var(--accent-soft);
    border-radius: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: var(--accent);
    margin: 0 auto 1.5rem;
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.skill-card:hover .category-icon {
    background: var(--accent);
    color: #fff;
    transform: rotate(10deg);
}

.group-name {
    font-family: 'Outfit', sans-serif;
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0;
}

.skills-pill-box {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    justify-content: center;
}

.skill-pill {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 1.2rem;
    background: var(--skill-pill-bg);
    border: 1px solid var(--border-color);
    border-radius: 999px;
    font-weight: 500;
    font-size: 0.9rem;
    color: var(--text-main);
    transition: all 0.3s ease;
}

.skill-pill i {
    font-size: 1rem;
    color: var(--accent);
}

.skill-card:hover .skill-pill {
    border-color: #e0e0e0;
}

.skill-pill:hover {
    background: var(--accent);
    color: #fff !important;
    border-color: var(--accent);
    transform: scale(1.1);
}

.skill-pill:hover i {
    color: #fff;
}

/* Footer */
.main-footer {
    padding: 1rem 0;
    border-top: 1px solid var(--border-color);
}

.footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.footer-copy {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin: 0;
}

.footer-socials {
    display: flex;
    gap: 1.5rem;
}

.footer-socials a {
    color: var(--text-main);
    font-size: 1.25rem;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.footer-socials a:hover {
    color: var(--accent);
    transform: translateY(-3px);
}

/* Animations */
.reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.reveal.active {
    opacity: 1;
    transform: translateY(0);
}

/* Responsive */
@media (max-width: 1024px) {
    .skills-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
    }

    .about-grid {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
    
    .hero-title {
        font-size: 3.5rem;
    }
}

@media (max-width: 767px) {
    .desktop-links {
        display: none !important;
    }

    .menu-toggle {
        display: flex !important;
        align-items: center;
        justify-content: center;
    }

    .skills-grid {
        grid-template-columns: 1fr;
    }

    .hero-title {
        font-size: 2.5rem;
    }
    
    .expertise-grid {
        grid-template-columns: 1fr;
    }
    
    .about-section, .skills-section {
        padding: 4rem 0;
    }

    .glass-nav {
        border-radius: 1.25rem !important;
        width: 92% !important;
        top: 1rem !important;
        padding: 0.5rem 1.25rem !important;
    }

    .nav-logo {
        font-size: 1.25rem;
    }

    .nav-open {
        border-radius: 1.25rem !important;
    }
}

.menu-toggle {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: var(--text-main);
    cursor: pointer;
    padding: 0.5rem;
    transition: var(--transition);
}

.mobile-links {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 0 1rem;
    border-top: 1px solid var(--border-color);
    margin-top: 0.5rem;
}

.mobile-links a {
    text-decoration: none;
    color: var(--text-main);
    font-weight: 500;
    font-size: 1.1rem;
    padding: 0.5rem 0;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.mobile-theme-toggle {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-start;
}
</style>
